<template>
  <div class="rates-page">
    <div class="rates-container">
      <header class="page-header">
        <div class="header-text">
          <h1 class="page-title">{{ $t('exchangeRates.title') }}</h1>
          <p class="page-subtitle">{{ $t('exchangeRates.subtitle') }}</p>
        </div>

        <label class="date-field">
          <span class="date-label">{{ $t('exchangeRates.date') }}</span>
          <!-- Capped at today, not at the last date we hold: BNB publishes
               the day's rates in the afternoon, so asking for today before
               then is normal and answered by the previous business day's.
               Capping at what we hold would make today unaskable. -->
          <DateField v-model="selectedDate" :max="maxDate" @change="loadRates" />
        </label>
      </header>

      <!-- Three facts that frame everything below: which day is on screen,
           what the lev is fixed at, and whether the feeds agree. Side by side
           because they are read together, not one after another. -->
      <div class="summary-strip">
        <div class="summary-tile">
          <span class="tile-label">{{ $t('exchangeRates.date') }}</span>
          <span class="tile-value">{{ formatDate(effectiveDate) || '—' }}</span>
          <span v-if="carriedForward" class="tile-note warn">
            {{ $t('exchangeRates.carriedShort') }}
          </span>
        </div>

        <div class="summary-tile pinned">
          <span class="tile-label">{{ $t('exchangeRates.sourceLabel') }}</span>
          <span class="tile-value">{{ rates.length }}</span>
          <span class="tile-note">{{ $t('exchangeRates.bnbNote', { count: rates.length }) }}</span>
        </div>

        <div v-for="entry in comparison" :key="entry.source" class="summary-tile" :class="entry.state">
          <span class="tile-label">{{ entry.label }}</span>
          <span class="tile-value">{{ entry.headline }}</span>
          <span class="tile-note">{{ entry.text }}</span>
        </div>
      </div>

      <!-- The whole sentence, below the strip rather than inside a tile:
           on a weekend or a holiday BNB publishes nothing and the previous
           business day's rates still apply, which takes a sentence to say. -->
      <p v-if="carriedForward" class="carried-note">
        {{ $t('exchangeRates.carriedForward', { date: formatDate(effectiveDate) }) }}
      </p>

      <!-- BNB rewrote its table when Bulgaria adopted the euro: up to
           30.12.2025 the rates are prices in leva, after it they are quoted
           against the euro. Which one is on screen changes what the columns
           mean, so it is said outright. -->
      <p v-if="isLevEra" class="era-note">{{ $t('exchangeRates.levEra') }}</p>

      <div v-if="loading" class="loading-state">
        <div class="spinner"></div>
        <p>{{ $t('common.loading') }}</p>
      </div>

      <div v-else-if="error" class="empty-state">
        <h3>{{ $t('exchangeRates.loadFailed') }}</h3>
        <p>{{ error }}</p>
      </div>

      <div v-else-if="rates.length === 0" class="empty-state">
        <h3>{{ $t('exchangeRates.noRates') }}</h3>
        <p>{{ $t('exchangeRates.noRatesDesc') }}</p>
      </div>

      <template v-else>
        <div class="rates-table">
          <table>
            <thead>
              <tr>
                <th>{{ $t('exchangeRates.code') }}</th>
                <th>{{ $t('exchangeRates.currency') }}</th>
                <!-- The lev era quotes a price for 1, 10, 100, 1000 or 10000
                     units, so that count is a column of its own; the euro era
                     always quotes per single unit and needs no such column. -->
                <th v-if="isLevEra" class="numeric">{{ $t('exchangeRates.quoteUnit') }}</th>
                <th class="numeric">{{ $t('exchangeRates.basePerQuote.' + base) }}</th>
                <th class="numeric">{{ $t('exchangeRates.fxPerBase.' + base) }}</th>
                <th v-for="feed in comparedFeeds" :key="feed.source" class="numeric">
                  {{ feedLabel(feed) }}
                </th>
              </tr>
            </thead>
            <tbody>
              <!-- The lev first, and set apart: it is fixed by law rather than
                   quoted, so it never changes and BNB does not list it. -->
              <tr v-if="!isLevEra" class="pinned-row">
                <td><span class="code-chip">BGN</span></td>
                <td class="name-cell">
                  {{ $t('exchangeRates.bgnName') }}
                  <span class="fixed-tag">{{ $t('exchangeRates.fixedTag') }}</span>
                </td>
                <td class="numeric">—</td>
                <td class="numeric strong">{{ bgnPerEur }}</td>
                <td v-for="feed in comparedFeeds" :key="feed.source" class="numeric">—</td>
              </tr>

              <tr v-for="rate in rates" :key="rate.code" :class="{ disagrees: rate.disagrees }">
                <td><span class="code-chip">{{ rate.code }}</span></td>
                <td class="name-cell">{{ currencyName(rate) }}</td>
                <td v-if="isLevEra" class="numeric unit">×{{ rate.quote_unit }}</td>
                <td class="numeric">{{ rate.base_per_quote || '—' }}</td>
                <td class="numeric strong">{{ rate.fx_per_base || '—' }}</td>
                <td
                  v-for="feed in comparedFeeds"
                  :key="feed.source"
                  class="numeric"
                  :class="{ mismatch: rate.disagrees }"
                >
                  {{ rate.alternates?.[feed.source] || '—' }}
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <p class="source-note">
          {{ $t('exchangeRates.sourceNote', { date: formatDate(effectiveDate) }) }}
          <a :href="source" target="_blank" rel="noopener">bnb.bg</a>
        </p>
      </template>
    </div>
  </div>
</template>

<script setup>
import DateField from '@/components/DateField.vue';
import { ref, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import api from '@/utils/api';

const { t, te, locale } = useI18n();

/*
 * BNB publishes the currency names in Bulgarian only, and the feeds name
 * themselves in Bulgarian too, so the table stayed Bulgarian whatever the
 * language was. The names come from the browser's own CLDR data, which knows
 * every ISO code in every language we ship; Bulgarian keeps BNB's own wording,
 * which is the authoritative one here.
 */
const displayNames = (lang) => {
  try {
    return new Intl.DisplayNames([lang], { type: 'currency' });
  } catch {
    return null;
  }
};

const currencyNames = computed(() =>
  locale.value === 'bg' ? [] : [displayNames(locale.value), displayNames('en')]
);

const currencyName = (rate) => {
  for (const names of currencyNames.value) {
    // Intl answers with the code itself when it knows no name for it — CLDR
    // leaves out the odd one, such as gold in Greek, and English covers those.
    const name = names?.of(rate.code);
    if (name && name !== rate.code) return name;
  }
  return rate.name || rate.code;
};

/** The feed's own label is Bulgarian; ours is translated where we know it. */
const feedLabel = (feed) => {
  const key = `exchangeRates.sources.${feed.source}`;
  return te(key) ? t(key) : feed.label;
};

const today = () => new Date().toISOString().slice(0, 10);

const selectedDate = ref(today());
const effectiveDate = ref(null);
const latestAvailable = ref(null);
const bgnPerEur = ref('1.95583');
const source = ref('https://www.bnb.bg/statistics/stexternalsector/stexchangerates/sterforeigncurrencies/index.htm');
const rates = ref([]);
const feeds = ref([]);
const base = ref('EUR');
const loading = ref(true);
const error = ref('');

const carriedForward = computed(
  () => effectiveDate.value && effectiveDate.value !== selectedDate.value
);

/** BNB quoted in leva up to 30.12.2025 and in euro from 05.01.2026. */
const isLevEra = computed(() => base.value === 'BGN');

const maxDate = computed(() => today());

const formatDate = (value) => {
  if (!value) return '';
  const [year, month, day] = value.split('-');
  return `${day}.${month}.${year}`;
};

/** Only the feeds that actually cover this date get a column of their own. */
const comparedFeeds = computed(() => feeds.value.filter((feed) => feed.comparable));

/** One line per feed saying whether it agrees, disagrees, or was not compared. */
/** One tile per feed: a headline for the glance, a sentence for the detail. */
const comparison = computed(() =>
  feeds.value.map((feed) => {
    const label = feedLabel(feed);

    if (!feed.comparable) {
      return {
        source: feed.source,
        label,
        state: 'info',
        headline: '—',
        text:
          feed.note === 'different_date'
            ? t('exchangeRates.feedBehind', { feed: label, date: formatDate(feed.date) })
            : t('exchangeRates.feedNoData', { feed: label }),
      };
    }

    if (feed.differences?.length) {
      return {
        source: feed.source,
        label,
        state: 'bad',
        headline: String(feed.differences.length),
        text: t('exchangeRates.feedDiffers', {
          feed: label,
          count: feed.differences.length,
          codes: feed.differences.map((d) => d.code).join(', '),
        }),
      };
    }

    return {
      source: feed.source,
      label,
      state: 'good',
      headline: String(feed.matches),
      text: t('exchangeRates.feedAgrees', { feed: label, count: feed.matches }),
    };
  })
);

const loadRates = async () => {
  loading.value = true;
  error.value = '';

  try {
    const { data } = await api.get('/api/exchange-rates', {
      params: { date: selectedDate.value },
    });

    rates.value = data.rates || [];
    feeds.value = data.comparison?.sources || [];
    effectiveDate.value = data.effective_date;
    latestAvailable.value = data.latest_available;
    base.value = data.base || 'EUR';
    if (data.bgn_per_eur) bgnPerEur.value = data.bgn_per_eur;
    if (data.source) source.value = data.source;
  } catch (e) {
    rates.value = [];
    feeds.value = [];
    error.value = e.response?.data?.message || e.message;
  } finally {
    loading.value = false;
  }
};

onMounted(loadRates);
</script>

<style scoped>
.rates-page {
  min-height: 100vh;
  background: var(--bg-gray);
  padding: 24px 20px;
}

.rates-container {
  max-width: 1366px;
  margin: 0 auto;
}

/* Header */
.page-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  gap: 20px;
  flex-wrap: wrap;
  margin-bottom: 20px;
}

.page-title {
  font-size: 22px;
  font-weight: 700;
  color: var(--text-primary);
  margin: 0 0 2px 0;
  letter-spacing: -0.02em;
}

.page-subtitle {
  font-size: 13px;
  color: var(--text-secondary);
  margin: 0;
}

.date-field {
  display: flex;
  align-items: center;
  gap: 10px;
}

.date-label {
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.6px;
  text-transform: uppercase;
  color: var(--text-light);
}

.date-field input {
  padding: 7px 12px;
  border: 1px solid var(--border);
  border-radius: 999px;
  font-size: 13px;
  color: var(--text-primary);
  background: #fff;
  font-family: inherit;
}

.date-field input:focus {
  outline: none;
  border-color: #2b82f0;
  box-shadow: 0 0 0 3px rgba(43, 130, 240, 0.12);
}

/* The strip of facts above the table */
.summary-strip {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  gap: 12px;
  margin-bottom: 16px;
}

.summary-tile {
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: 12px 16px;
  /* A grid item will not shrink below its own content unless told to, and a
     sentence with no break points pushed this tile out of its track and over
     the one beside it. */
  min-width: 0;
  background: #fff;
  border: 1px solid var(--border);
  /* A coloured edge rather than a coloured fill: the tiles sit in a row and
     three filled blocks would read as three warnings. */
  border-left: 3px solid var(--border);
  border-radius: 10px;
}

/* 'pinned', not 'fixed': Tailwind is loaded globally and owns .fixed as
   position: fixed, which took this tile out of the flow and left the one
   before it with nowhere to sit. A modifier here has to be a word no utility
   class already claims. */
.summary-tile.pinned { border-left-color: #2b82f0; }
.summary-tile.good { border-left-color: #10b981; }
.summary-tile.bad { border-left-color: #ef4444; }
.summary-tile.info { border-left-color: #cbd5e1; }

.tile-label {
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.6px;
  text-transform: uppercase;
  color: var(--text-light);
}

.tile-value {
  font-size: 19px;
  font-weight: 700;
  color: var(--text-primary);
  font-variant-numeric: tabular-nums;
  line-height: 1.2;
  overflow-wrap: anywhere;
}

.summary-tile.bad .tile-value { color: #b91c1c; }
.summary-tile.good .tile-value { color: #047857; }

.tile-note {
  font-size: 11px;
  color: var(--text-secondary);
  line-height: 1.35;
  overflow-wrap: anywhere;
}

.tile-note.warn { color: #92400e; }

.carried-note {
  margin: 0 0 12px 0;
  padding: 9px 14px;
  background: #fffbeb;
  border: 1px solid #fde68a;
  border-radius: 10px;
  font-size: 12px;
  line-height: 1.45;
  color: #92400e;
}

.era-note {
  margin: 0 0 16px 0;
  padding: 10px 14px;
  background: #fffbeb;
  border: 1px solid #fde68a;
  border-radius: 10px;
  font-size: 12px;
  line-height: 1.45;
  color: #854d0e;
}

/* Table */
.rates-table {
  background: #fff;
  border: 1px solid var(--border);
  border-radius: 12px;
  overflow: hidden;
}

table {
  width: 100%;
  border-collapse: collapse;
}

thead {
  background: linear-gradient(180deg, #fafbfc 0%, #f4f6f8 100%);
}

th {
  text-align: left;
  padding: 10px 18px;
  font-size: 10px;
  font-weight: 700;
  color: var(--text-light);
  text-transform: uppercase;
  letter-spacing: 0.6px;
  white-space: nowrap;
  border-bottom: 1px solid var(--border);
}

th.numeric { text-align: right; }

tbody tr { border-bottom: 1px solid #eef1f4; }
tbody tr:last-child { border-bottom: none; }
tbody tr:hover { background: #f8fafc; }

td {
  padding: 8px 18px;
  font-size: 13px;
  color: var(--text-primary);
}

/* Figures line up digit over digit, which is the whole point of reading a
   column of rates. */
.numeric {
  text-align: right;
  font-variant-numeric: tabular-nums;
  font-feature-settings: 'tnum';
}

.numeric.strong {
  font-weight: 700;
}

.unit {
  color: var(--text-light);
  font-size: 12px;
}

.name-cell {
  color: var(--text-secondary);
}

/* The code as an outlined chip, the same shape the language control uses. */
.code-chip {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 42px;
  padding: 2px 8px;
  border: 1.5px solid rgba(43, 130, 240, 0.35);
  border-radius: 6px;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.04em;
  color: #2b82f0;
}

/* The lev's row, held apart from the quoted ones by tone rather than by a
   heavier line: it belongs in the table but is not of the same kind. */
.pinned-row {
  background: #f8fbff;
}

.pinned-row:hover {
  background: #f1f7fe;
}

.fixed-tag {
  display: inline-block;
  margin-left: 6px;
  padding: 1px 6px;
  border-radius: 999px;
  background: rgba(43, 130, 240, 0.1);
  color: #2b82f0;
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.4px;
}

tbody tr.disagrees { background: #fef2f2; }
tbody tr.disagrees:hover { background: #fee2e2; }
td.mismatch { color: #b91c1c; font-weight: 700; }

.source-note {
  margin: 12px 0 0 0;
  font-size: 12px;
  color: var(--text-light);
}

.source-note a {
  color: #2b82f0;
  text-decoration: none;
  font-weight: 600;
}

.source-note a:hover { text-decoration: underline; }

/* States */
.loading-state,
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  padding: 48px 20px;
  background: #fff;
  border: 1px solid var(--border);
  border-radius: 12px;
  text-align: center;
}

.empty-state h3 {
  margin: 0;
  font-size: 16px;
  color: var(--text-primary);
}

.empty-state p {
  margin: 0;
  font-size: 13px;
  color: var(--text-secondary);
  max-width: 420px;
}

.spinner {
  width: 30px;
  height: 30px;
  border: 3px solid var(--border);
  border-top-color: #2b82f0;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

@media (max-width: 720px) {
  .page-header { flex-direction: column; align-items: stretch; }
  th, td { padding: 8px 12px; }
  .name-cell { display: none; }
}
</style>
